import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Receipt, Upload, X } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import { useCreateInventoryReimbursement } from "@/hooks/useInventoryReimbursements";
import { useInventoryItems } from "@/hooks/useInventory";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export interface ReimbursementModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  propertyId: string;
  cleaningTaskId: string | null;
  createdByUserId: string;
  billToType: "owner_to_cleaner" | "owner_to_company" | "none";
  billToId: string | null;
  inventoryAutopayEnabled: boolean;
  /** Prefill item name when opened from an inventory item */
  defaultItemName?: string;
  /** Pre-select this inventory item (increments Ordered (not arrived) on submit) */
  defaultInventoryItemId?: string | null;
  onSuccess?: () => void;
}

export function ReimbursementModal({
  open,
  onOpenChange,
  propertyId,
  cleaningTaskId,
  createdByUserId,
  billToType,
  billToId,
  inventoryAutopayEnabled,
  defaultItemName = "",
  defaultInventoryItemId = null,
  onSuccess,
}: ReimbursementModalProps) {
  const { tDynamic } = useTranslation();
  const createReimbursement = useCreateInventoryReimbursement();
  const { data: inventoryItems } = useInventoryItems(propertyId);
  const [itemName, setItemName] = useState("");
  const [description, setDescription] = useState("");
  const [amountDollars, setAmountDollars] = useState("");
  const [quantityPurchased, setQuantityPurchased] = useState(1);
  const [inventoryItemId, setInventoryItemId] = useState<string | null>(null);
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [paidImmediately, setPaidImmediately] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (open) {
      setItemName(defaultItemName);
      setDescription("");
      setAmountDollars("");
      setQuantityPurchased(1);
      setInventoryItemId(defaultInventoryItemId ?? null);
      setReceiptFile(null);
      setPaidImmediately(false);
    }
  }, [open, defaultItemName, defaultInventoryItemId]);

  const uploadReceipt = async (file: File): Promise<string | null> => {
    const fileExt = file.name.split(".").pop() || "jpg";
    const fileName = `${propertyId}/reimbursements/${Date.now()}.${fileExt}`;
    const { error } = await supabase.storage.from("cleaning-photos").upload(fileName, file);
    if (error) return null;
    const { data: urlData } = supabase.storage.from("cleaning-photos").getPublicUrl(fileName);
    return urlData.publicUrl;
  };

  const handleSubmit = async () => {
    const name = itemName.trim();
    if (!name) {
      toast({ title: tDynamic("Item name required"), variant: "destructive" });
      return;
    }
    const qty = Math.floor(quantityPurchased);
    if (!Number.isFinite(qty) || qty < 1) {
      toast({ title: tDynamic("Quantity purchased must be at least 1"), variant: "destructive" });
      return;
    }
    const amount = parseFloat(amountDollars);
    if (isNaN(amount) || amount <= 0) {
      toast({ title: tDynamic("Enter a valid amount"), variant: "destructive" });
      return;
    }
    if (!receiptFile) {
      toast({ title: tDynamic("Receipt required"), variant: "destructive" });
      return;
    }
    setUploading(true);
    try {
      const url = await uploadReceipt(receiptFile);
      if (!url) {
        toast({ title: tDynamic("Failed to upload receipt"), variant: "destructive" });
        return;
      }
      const amountCents = Math.round(amount * 100);
      await createReimbursement.mutateAsync({
        propertyId,
        cleaningTaskId,
        createdByUserId,
        submittedByRole: "cleaner",
        billToType,
        billToId,
        amountCents,
        description: description.trim() || name,
        receiptUrls: [url],
        paidImmediately: inventoryAutopayEnabled && paidImmediately,
        quantity: qty,
        inventoryItemId: inventoryItemId || undefined,
      });
      toast({
        title: paidImmediately ? tDynamic("Reimbursement recorded (paid)") : tDynamic("Reimbursement submitted"),
      });
      onOpenChange(false);
      onSuccess?.();
    } catch (e) {
      toast({ title: tDynamic("Error"), description: (e as Error).message, variant: "destructive" });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md flex max-h-[85vh] flex-col p-0 gap-0">
        <DialogHeader className="shrink-0 px-6 pt-6 pb-2">
          <DialogTitle>{tDynamic("Add purchase / reimbursement")}</DialogTitle>
          <DialogDescription>
            {tDynamic("Log an inventory purchase for this turnover. Receipt required.")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2 px-6 overflow-y-auto min-h-0 flex-1 overscroll-contain touch-pan-y">
          <div>
            <Label htmlFor="reimb-item">{tDynamic("Item name")}</Label>
            <Input
              id="reimb-item"
              value={itemName}
              onChange={(e) => setItemName(e.target.value)}
              placeholder={tDynamic("e.g. Paper towels")}
              className="mt-1.5"
            />
          </div>
          <div>
            <Label htmlFor="reimb-qty">{tDynamic("Quantity purchased")}</Label>
            <Input
              id="reimb-qty"
              type="number"
              min={1}
              value={quantityPurchased}
              onChange={(e) => setQuantityPurchased(Math.max(1, parseInt(e.target.value) || 1))}
              className="mt-1.5"
            />
          </div>
          {inventoryItems && inventoryItems.length > 0 && (
            <div>
              <Label>{tDynamic("Link to inventory item (optional)")}</Label>
              <Select value={inventoryItemId ?? "none"} onValueChange={(v) => setInventoryItemId(v === "none" ? null : v)}>
                <SelectTrigger className="mt-1.5">
                  <SelectValue placeholder={tDynamic("None")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{tDynamic("None")}</SelectItem>
                  {inventoryItems.map((i) => (
                    <SelectItem key={i.id} value={i.id}>{i.item_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                {tDynamic("If selected, this purchase will increase \"Ordered (not arrived)\" for that item.")}
              </p>
            </div>
          )}
          <div>
            <Label htmlFor="reimb-desc">{tDynamic("Description / notes")}</Label>
            <Textarea
              id="reimb-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={tDynamic("Optional details")}
              className="mt-1.5"
              rows={2}
            />
          </div>
          <div>
            <Label htmlFor="reimb-amount">{tDynamic("Amount ($)")}</Label>
            <Input
              id="reimb-amount"
              type="number"
              step="0.01"
              min="0"
              value={amountDollars}
              onChange={(e) => setAmountDollars(e.target.value)}
              placeholder="0.00"
              className="mt-1.5"
            />
          </div>
          <div>
            <Label>{tDynamic("Receipt")}</Label>
            <div className="mt-1.5">
              {receiptFile ? (
                <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                  <Receipt className="w-5 h-5 text-muted-foreground" />
                  <span className="flex-1 truncate text-sm">{receiptFile.name}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setReceiptFile(null)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <label className="flex items-center justify-center gap-2 h-12 border-2 border-dashed rounded-lg cursor-pointer hover:bg-muted/50">
                  <Upload className="w-5 h-5 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">{tDynamic("Upload receipt")}</span>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => setReceiptFile(e.target.files?.[0] || null)}
                  />
                </label>
              )}
            </div>
          </div>
          {inventoryAutopayEnabled && (
            <div className="flex items-center space-x-2 rounded-lg border p-3">
              <Checkbox
                id="reimb-autopay"
                checked={paidImmediately}
                onCheckedChange={(v) => setPaidImmediately(v === true)}
              />
              <Label htmlFor="reimb-autopay" className="text-sm font-normal cursor-pointer">
                {tDynamic("Paid immediately (autopay)")}
              </Label>
            </div>
          )}
        </div>
        <DialogFooter className="shrink-0 px-6 pb-6 pt-2 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {tDynamic("Cancel")}
          </Button>
          <Button onClick={handleSubmit} disabled={createReimbursement.isPending || uploading}>
            {uploading || createReimbursement.isPending
              ? tDynamic("Submitting...")
              : tDynamic("Submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
